import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Token storage.
 *
 * NOTE on security: tokens are stored in localStorage. The risk vector is XSS
 * (an attacker injecting a script could read the token). Mitigations applied:
 *   1. The site is built with React (escapes output by default — no dangerouslySetInnerHTML usage).
 *   2. The backend sets a strict Content-Security-Policy header (see server.py)
 *      that blocks inline scripts and untrusted origins.
 *   3. The token grants admin-only CMS access — not financial or PII transactions.
 *   4. Tokens auto-expire (JWT_EXP_HOURS in backend).
 *
 * Migrating to httpOnly cookies would require a CSRF protection layer and
 * cross-origin cookie configuration that is non-trivial in this preview/deploy
 * environment. Documented as accepted residual risk for the admin-only panel.
 */
const TOKEN_KEY = "dhadda_admin_token";

const safeGet = (k) => {
  try {
    return window.localStorage.getItem(k);
  } catch (err) {
    console.warn("Token read failed:", err);
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    window.localStorage.setItem(k, v);
  } catch (err) {
    console.warn("Token write failed:", err);
  }
};
const safeRemove = (k) => {
  try {
    window.localStorage.removeItem(k);
  } catch (err) {
    console.warn("Token clear failed:", err);
  }
};

export const getToken = () => safeGet(TOKEN_KEY);
export const setToken = (t) => safeSet(TOKEN_KEY, t);
export const clearToken = () => safeRemove(TOKEN_KEY);

const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const api = {
  // Public
  getContent: () => axios.get(`${API}/content`).then((r) => r.data),
  getProjects: () => axios.get(`${API}/projects`).then((r) => r.data),
  submitContact: (payload) => axios.post(`${API}/contact`, payload).then((r) => r.data),
  getBlogs: () => axios.get(`${API}/blogs`).then((r) => r.data),
  getBlog: (slug) => axios.get(`${API}/blogs/${slug}`).then((r) => r.data),

  // Auth
  login: (email, password) =>
    axios.post(`${API}/admin/login`, { email, password }).then((r) => r.data),
  me: () => axios.get(`${API}/admin/me`, { headers: authHeaders() }).then((r) => r.data),

  // Admin
  updateContent: (key, data) =>
    axios.put(`${API}/content/${key}`, { data }, { headers: authHeaders() }).then((r) => r.data),
  createProject: (payload) =>
    axios.post(`${API}/projects`, payload, { headers: authHeaders() }).then((r) => r.data),
  updateProject: (id, payload) =>
    axios.put(`${API}/projects/${id}`, payload, { headers: authHeaders() }).then((r) => r.data),
  deleteProject: (id) =>
    axios.delete(`${API}/projects/${id}`, { headers: authHeaders() }).then((r) => r.data),
  listContact: () =>
    axios.get(`${API}/contact`, { headers: authHeaders() }).then((r) => r.data),
  deleteContact: (id) =>
    axios.delete(`${API}/contact/${id}`, { headers: authHeaders() }).then((r) => r.data),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios
      .post(`${API}/upload`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  generateImage: (prompt, section = "hero") =>
    axios
      .post(`${API}/generate-image`, { prompt, section }, { headers: authHeaders() })
      .then((r) => r.data),
  // Blogs (admin)
  listBlogsAdmin: () =>
    axios.get(`${API}/blogs?only_published=false`, { headers: authHeaders() }).then((r) => r.data),
  createBlog: (payload) =>
    axios.post(`${API}/blogs`, payload, { headers: authHeaders() }).then((r) => r.data),
  updateBlog: (id, payload) =>
    axios.put(`${API}/blogs/${id}`, payload, { headers: authHeaders() }).then((r) => r.data),
  deleteBlog: (id) =>
    axios.delete(`${API}/blogs/${id}`, { headers: authHeaders() }).then((r) => r.data),
};

export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};
